import { z } from 'zod'
import { aboveFoldSectionAdsSchema } from '../../components/Sections/types/index.types'

export type DeepPartial<T> = {
  [P in keyof T]?: DeepPartial<T[P]>
}

// === Zod Schemas ===

// Sponsored Tiles

const sponsoredTilesSchema = z.object({
  provider: z.union([
    z.literal('affinity'),
    z.literal('admarketplace'),
    z.literal('off'),
  ]),
  tilesCount: z.number().default(5),
  design: z.union([z.literal('msn'), z.literal('google')]).optional(),
  variant: z
    .union([
      z.literal('default'),
      z.literal('circle'),
      z.literal('large-primary'),
      z.literal('large-secondary'),
    ])
    .optional(),
})

// Tiles Providers
const tilesProviderSchema = z.object({
  provider: z.union([z.literal('affinity'), z.literal('admarketplace')]),
  tilesCount: z.number().default(5),
})

const directTilesProviderSchema = z.object({
  provider: z.literal('direct'),
  name: z.string(),
  image_url: z.string(),
  click_url: z.string(),
  impression_url: z.string(),
})

const dynamicSponsoredTilesSchema = z.object({
  tilesProvider: z.array(
    z.union([tilesProviderSchema, directTilesProviderSchema])
  ),
  design: z.union([z.literal('msn'), z.literal('google')]).optional(),
  variant: z
    .union([
      z.literal('default'),
      z.literal('circle'),
      z.literal('large-primary'),
      z.literal('large-secondary'),
    ])
    .optional(),
})

const dynamicFloorPricingSchema = z.object({
  isEnabled: z.boolean().default(true),
  excludePlacements: z.array(z.string()).optional().default([]),
})

// User Settings
const userSettingsSchema = z.object({
  position: z
    .union([z.literal('off'), z.literal('footer'), z.literal('navbar')])
    .default('off')
    .transform((value) => {
      if (value === 'navbar') return 'footer'
      return value
    }),
  defaultSettings: z.object({
    theme: z.union([z.literal('light'), z.literal('dark'), z.literal('auto')]),
    articleSummary: z.boolean().default(true),
  }),
  enabledSettings: z
    .object({
      articleSummary: z.boolean().default(false),
    })
    .default({ articleSummary: false }),
})

// Home + Summary Layout
const homePageLayoutSchema = z.object({
  doodle: z.union([z.literal('off'), z.literal('on'), z.literal('random')]),
  design: z.optional(z.union([z.literal('google'), z.literal('msn')])),
  searchboxDesign: z.optional(
    z.union([
      z.literal('default'),
      z.literal('v2'),
      z.literal('v2-big-yolo'),
      z.literal('v3'),
    ])
  ),
})

const articleGroupDetailsSchema = z.object({
  design: z.union([z.literal('google'), z.literal('msn')]).optional(),
  articlesPerGroup: z.number().optional().default(3),
  type: z
    .union([z.literal('top-stories'), z.literal('related-stories')])
    .optional()
    .default('top-stories'),
})

const summaryPageLayoutSchema = z.object({
  design: z.union([z.literal('google'), z.literal('msn')]).optional(),
  siderail: z
    .object({
      position: z
        .union([z.literal('below-header'), z.literal('full-height')])
        .optional(),
      topArticleGroup: articleGroupDetailsSchema.optional(),
      bottomArticleGroup: articleGroupDetailsSchema.optional(),
    })
    .optional(),
  showArticleImage: z.boolean().optional().default(true),
})

// === Partitioned ContentDisplaySettings Schema ===

export const adSettingsSchema = z.object({
  sponsoredTiles: z.optional(sponsoredTilesSchema),
  dynamicSponsoredTiles: z.optional(dynamicSponsoredTilesSchema),
  aboveFoldSectionAds: z.optional(aboveFoldSectionAdsSchema),
  aboveFoldSectionLeaderboardAd: z
    .union([
      z.literal('on_testsection_leader'),
      z.literal('sn_testsection_leader'),
      z.literal('gd_testsection_leader'),
      z.literal(''),
    ])
    .default('')
    .optional(),
  adsDynamicFloorPricing: z.optional(dynamicFloorPricingSchema),
  summaryPage: z.optional(
    z.object({
      showSiderailTopAdPlacement: z.boolean().optional(),
      showSiderailMiddleAdPlacement: z.boolean().optional(),
      showSiderailBottomAdPlacement: z.boolean().optional(),
      showContentHeroAdPlacement: z.boolean().optional(),
      showContentTopAdPlacement: z.boolean().optional(),
      showContentBelowImageAdPlacement: z.boolean().optional(),
      showContentMiddleAdPlacement: z.boolean().optional(),
      showContentAboveButtonAdPlacement: z.boolean().optional(),
      showContentBelowButtonAdPlacement: z.boolean().optional(),
    })
  ),
})

export const siteSettingsSchema = z.object({
  userSettings: z.optional(userSettingsSchema),
  enableLocalNewsSearch: z.optional(z.boolean()).default(true),
  sortNewsBy: z
    .optional(z.union([z.literal('publishedDate'), z.literal('clusterSize')]))
    .default('publishedDate'),
  enableArticleReordering: z.optional(z.boolean()),
  videoEnabled: z.optional(z.boolean()),
})

export const pageLayoutSettingsSchema = z.object({
  homePage: z.optional(homePageLayoutSchema.partial()),
  summaryPage: z.optional(summaryPageLayoutSchema),
})

export const featureSplitFlagsSchema = z.object({
  ayData: z.unknown(),
  timestamp: z.optional(z.string()),
  name: z.optional(z.string()),
  aboveFoldSectionShown: z.optional(
    z.union([
      z.literal('LargeCardWithGridSection'),
      z.literal('HalfPageAdWithGridSection'),
    ])
  ),
})

// Unified ContentDisplaySettings Schema
export const contentDisplaySettingsSchema = siteSettingsSchema
  .and(pageLayoutSettingsSchema)
  .and(featureSplitFlagsSchema)

// === Types ===
export type TilesProvider =
  | z.infer<typeof tilesProviderSchema>
  | z.infer<typeof directTilesProviderSchema>
export type ArticleGroupDetails = z.infer<typeof articleGroupDetailsSchema>
export type AdSettings = z.infer<typeof adSettingsSchema>
export type SiteSettings = z.infer<typeof siteSettingsSchema>
export type LayoutSettings = z.infer<typeof pageLayoutSettingsSchema>
export type ContentDisplaySettings = z.infer<
  typeof contentDisplaySettingsSchema
>
export type DynamicSponsoredTiles = z.infer<typeof dynamicSponsoredTilesSchema>
